import { useState, useEffect } from 'react';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';

const MARKET_MODELS = [
  { key: 'feed_in_tariff', label: 'Feed-in Tariff' },
  { key: 'ppa', label: 'PPA (Power Purchase Agreement)' },
  { key: 'market_trading', label: 'Market Trading' },
  { key: 'self_consumption', label: 'Self Consumption' },
  { key: 'hybrid', label: 'Hybrid' },
];

interface MarketConfig {
  marketModel?: string;
  balancingGroup?: string;
  imbalanceResponsibleParty?: string;
  tradingZone?: string;
  settlementModel?: string;
}

interface Props {
  assetId: string;
  initialData?: MarketConfig;
  onSaved: () => void;
}

export default function TabMarket({ assetId, initialData, onSaved }: Props) {
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [form, setForm] = useState({
    marketModel: '',
    balancingGroup: '',
    imbalanceResponsibleParty: '',
    tradingZone: '',
    settlementModel: '',
  });

  useEffect(() => {
    if (initialData) {
      setForm({
        marketModel: initialData.marketModel ?? '',
        balancingGroup: initialData.balancingGroup ?? '',
        imbalanceResponsibleParty: initialData.imbalanceResponsibleParty ?? '',
        tradingZone: initialData.tradingZone ?? '',
        settlementModel: initialData.settlementModel ?? '',
      });
    }
  }, [initialData]);

  const handleSave = async () => {
    setSaving(true);
    setApiError(null);
    try {
      const payload: Record<string, unknown> = {};
      if (form.marketModel) payload.marketModel = form.marketModel;
      if (form.balancingGroup) payload.balancingGroup = form.balancingGroup;
      if (form.imbalanceResponsibleParty) payload.imbalanceResponsibleParty = form.imbalanceResponsibleParty;
      if (form.tradingZone) payload.tradingZone = form.tradingZone;
      if (form.settlementModel) payload.settlementModel = form.settlementModel;

      await api.patch(`/assets/${assetId}/market-config`, payload);
      addToast('Market configuration saved successfully', 'success');
      onSaved();
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to save market config');
      setApiError(message);
      addToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const inputBg = isDark ? '#1A1A1A' : '#fff';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const sectionBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';

  const inputClass = "w-full px-3 py-2 rounded-md text-sm outline-none focus:ring-1 focus:ring-[#D12229] transition-all whitespace-nowrap";
  const inputStyle: React.CSSProperties = {
    background: inputBg,
    border: `1px solid ${inputBorder}`,
    color: textMain,
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(209,34,41,0.08)' }}>
            <i className="ri-line-chart-line text-sm" style={{ color: '#D12229' }}></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: textMain }}>Market Configuration</h3>
            <p className="text-xs" style={{ color: '#6B6B70' }}>Trading model, balancing group and settlement settings</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
          style={{ background: '#D12229' }}
        >
          {saving ? (
            <><i className="ri-loader-4-line animate-spin"></i> Saving...</>
          ) : (
            <><i className="ri-save-line"></i> Save</>
          )}
        </button>
      </div>

      {apiError && (
        <div className="mb-4 px-4 py-3 rounded-lg text-sm" style={{ background: 'rgba(209,34,41,0.06)', border: '1px solid rgba(209,34,41,0.2)', color: '#D12229' }}>
          <i className="ri-error-warning-line mr-2"></i>{apiError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>Market Model</label>
          <select
            value={form.marketModel}
            onChange={(e) => setForm((prev) => ({ ...prev, marketModel: e.target.value }))}
            className={inputClass}
            style={inputStyle}
          >
            <option value="">Select market model...</option>
            {MARKET_MODELS.map((m) => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </select>
          {form.marketModel && (
            <div className="mt-2 px-3 py-2 rounded-md text-xs" style={{ background: sectionBg, color: textMuted }}>
              {form.marketModel === 'feed_in_tariff' && 'Fixed tariff paid by the grid operator for all generated electricity.'}
              {form.marketModel === 'ppa' && 'Long-term contract to sell electricity at a fixed or indexed price to a buyer.'}
              {form.marketModel === 'market_trading' && 'Electricity sold on the day-ahead or intraday market at spot prices.'}
              {form.marketModel === 'self_consumption' && 'Generated electricity consumed on-site, excess may be exported.'}
              {form.marketModel === 'hybrid' && 'Combination of multiple market models.'}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>Balancing Group</label>
          <input
            type="text"
            value={form.balancingGroup}
            onChange={(e) => setForm((prev) => ({ ...prev, balancingGroup: e.target.value }))}
            placeholder="e.g. BG-UA-HELIOS-01"
            className={inputClass}
            style={inputStyle}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>Imbalance Responsible Party</label>
          <input
            type="text"
            value={form.imbalanceResponsibleParty}
            onChange={(e) => setForm((prev) => ({ ...prev, imbalanceResponsibleParty: e.target.value }))}
            placeholder="e.g. Helios Aggregate LLC"
            className={inputClass}
            style={inputStyle}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>Trading Zone</label>
          <input
            type="text"
            value={form.tradingZone}
            onChange={(e) => setForm((prev) => ({ ...prev, tradingZone: e.target.value }))}
            placeholder="e.g. UA-IPS, UA-BEI"
            className={inputClass}
            style={inputStyle}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>Settlement Model</label>
          <input
            type="text"
            value={form.settlementModel}
            onChange={(e) => setForm((prev) => ({ ...prev, settlementModel: e.target.value }))}
            placeholder="e.g. Monthly, Hourly"
            className={inputClass}
            style={inputStyle}
          />
        </div>
      </div>
    </div>
  );
}
